import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Department {
  id: number;
  name: string;
  name_ar: string;
  name_en: string;
}

interface Participant {
  id: number;
  civilid: string;
  name: string;
  phone?: string;
  department?: number;
  department_name_ar?: string;
  department_name_en?: string;
  created_at: string;
  updated_at: string;
  surveys_count?: number;
}

interface SurveyParticipant {
  id: number;
  survey_number: string;
  date_collected: string;
  crop_name_ar: string;
  crop_name_en: string;
  village_name_ar: string;
  village_name_en: string;
  full_location_path: string;
  is_local_seed: boolean;
  has_photos: boolean;
}

const ParticipantsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSurveysDialog, setOpenSurveysDialog] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [participantSurveys, setParticipantSurveys] = useState<SurveyParticipant[]>([]);
  const [loadingSurveys, setLoadingSurveys] = useState(false);
  const [formData, setFormData] = useState({
    civilid: '',
    name: '',
    phone: '',
    department: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0
  });

  useEffect(() => {
    fetchParticipants();
    fetchDepartments();
  }, []);

  const fetchParticipants = async () => {
    try {
      setLoading(true);
      const response = await api.get('/seeds/people/');
      setParticipants(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching participants:', error);
      setSnackbar({
        open: true,
        message: t('errorFetchingParticipants'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments/');
      setDepartments(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchParticipantSurveys = async (participantId: number) => {
    try {
      setLoadingSurveys(true);
      const response = await api.get(`/seeds/people/${participantId}/surveys/`);
      setParticipantSurveys(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching participant surveys:', error);
      setSnackbar({
        open: true,
        message: t('errorFetchingSurveys'),
        severity: 'error',
      });
    } finally {
      setLoadingSurveys(false);
    }
  };

  const getDepartmentName = (participant: Participant): string => {
    if (participant.department_name_ar && participant.department_name_en) {
      return i18n.language === 'ar' ? participant.department_name_ar : participant.department_name_en;
    }
    
    if (participant.department) {
      const dept = departments.find(d => d.id === participant.department);
      if (dept) {
        return i18n.language === 'ar' ? dept.name_ar : dept.name_en;
      }
    }
    
    return t('noDepartment');
  };

  const handleOpenDialog = (participant?: Participant) => {
    if (participant) {
      setSelectedParticipant(participant);
      setFormData({
        civilid: participant.civilid,
        name: participant.name,
        phone: participant.phone || '',
        department: participant.department ? String(participant.department) : '',
      });
    } else {
      setSelectedParticipant(null);
      setFormData({
        civilid: '',
        name: '',
        phone: '',
        department: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedParticipant(null);
    setFormData({
      civilid: '',
      name: '',
      phone: '',
      department: '',
    });
  };

  const handleSave = async () => {
    try {
      if (!formData.civilid || !formData.name) {
        setSnackbar({
          open: true,
          message: t('requiredFieldsMissing'),
          severity: 'warning',
        });
        return;
      }

      const dataToSend = {
        ...formData,
        department: formData.department ? parseInt(formData.department) : null,
      };

      if (selectedParticipant) {
        await api.patch(`/seeds/people/${selectedParticipant.id}/`, dataToSend);
        setSnackbar({
          open: true,
          message: t('participantUpdated'),
          severity: 'success',
        });
      } else {
        await api.post('/seeds/people/', dataToSend);
        setSnackbar({
          open: true,
          message: t('participantAdded'),
          severity: 'success',
        });
      }
      handleCloseDialog();
      fetchParticipants();
    } catch (error: any) {
      console.error('Error saving participant:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.civilid?.[0] || t('errorSavingParticipant'),
        severity: 'error',
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedParticipant) return;
    
    try {
      await api.delete(`/seeds/people/${selectedParticipant.id}/`);
      setSnackbar({
        open: true,
        message: t('participantDeleted'),
        severity: 'success',
      });
      setOpenDeleteDialog(false);
      setSelectedParticipant(null);
      fetchParticipants();
    } catch (error) {
      console.error('Error deleting participant:', error);
      setSnackbar({
        open: true,
        message: t('errorDeletingParticipant'),
        severity: 'error',
      });
    }
  };

  const handleViewSurveys = (participant: Participant) => {
    setSelectedParticipant(participant);
    setOpenSurveysDialog(true);
    fetchParticipantSurveys(participant.id);
  };

  const filteredParticipants = participants.filter(participant =>
    participant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    participant.civilid.includes(searchTerm) ||
    (participant.phone && participant.phone.includes(searchTerm))
  );

  const columns: GridColDef[] = [
    {
      field: 'civilid',
      headerName: t('civilId'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BadgeIcon fontSize="small" color="primary" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon fontSize="small" color="success" />
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: t('phone'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('noPhone')}
          </Typography>
        )
      ),
    },
    {
      field: 'department',
      headerName: t('department'),
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        const deptName = getDepartmentName(params.row);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon fontSize="small" color="action" />
            <Typography variant="body2">{deptName}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'surveys_count',
      headerName: t('surveysCount'),
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 0}
          color={params.value > 0 ? 'primary' : 'default'}
          size="small"
          onClick={() => params.value > 0 && handleViewSurveys(params.row)}
          sx={{ cursor: params.value > 0 ? 'pointer' : 'default' }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: t('createdAt'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(params.value).toLocaleDateString(
            i18n.language === 'ar' ? 'ar-SA' : 'en-US'
          )}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t('viewSurveys')}>
            <IconButton
              size="small"
              color="info"
              onClick={() => handleViewSurveys(params.row)}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('editParticipant')}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenDialog(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('deleteParticipant')}>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setSelectedParticipant(params.row);
                setOpenDeleteDialog(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          {t('participantsManagement')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('participantsPageDescription')}
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PeopleIcon fontSize="large" color="primary" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {participants.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('totalParticipants')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon fontSize="large" color="info" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {departments.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('totalDepartments')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Actions Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder={t('searchParticipants')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(45deg, #2196F3 30%, #64B5F6 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
            }}
          >
            {t('addParticipant')}
          </Button>
        </Box>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredParticipants}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50]}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.paper',
              borderBottom: '2px solid',
              borderColor: 'primary.main',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedParticipant ? t('editParticipant') : t('addParticipant')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('civilId')}
              value={formData.civilid}
              onChange={(e) => setFormData({ ...formData, civilid: e.target.value })}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('phone')}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth>
              <InputLabel>{t('department')}</InputLabel>
              <Select
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                label={t('department')}
              >
                <MenuItem value="">
                  <em>{t('selectDepartment')}</em>
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {i18n.language === 'ar' ? dept.name_ar : dept.name_en}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {selectedParticipant ? t('update') : t('add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('deleteParticipant')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('confirmDeleteParticipant')} "{selectedParticipant?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Surveys Dialog */}
      <Dialog
        open={openSurveysDialog}
        onClose={() => setOpenSurveysDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {t('participantSurveys')}: {selectedParticipant?.name}
            </Typography>
            <IconButton onClick={() => setOpenSurveysDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingSurveys ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : participantSurveys.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('surveyNumber')}</TableCell>
                    <TableCell>{t('dateCollected')}</TableCell>
                    <TableCell>{t('crop')}</TableCell>
                    <TableCell>{t('village')}</TableCell>
                    <TableCell align="center">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participantSurveys.map((survey) => (
                    <TableRow key={survey.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {survey.survey_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(survey.date_collected).toLocaleDateString(
                          i18n.language === 'ar' ? 'ar-SA' : 'en-US'
                        )}
                      </TableCell>
                      <TableCell>
                        {i18n.language === 'ar' ? survey.crop_name_ar : survey.crop_name_en}
                      </TableCell>
                      <TableCell>
                        {i18n.language === 'ar' ? survey.village_name_ar : survey.village_name_en}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t('viewDetails')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setOpenSurveysDialog(false);
                              navigate(`/survey/details/${survey.id}`);
                            }}
                          >
                            <ArrowForwardIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
              {t('noSurveysFound')}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ParticipantsPage;
